import { createContext, useContext, useMemo } from "react";
import { getLocalTimeZone } from "@internationalized/date";
import { invariant } from "@pexip-engage/utils";
import { SerializeFrom } from "@remix-run/node";
import { useHydrated } from "remix-utils/use-hydrated";

import { createStateManager, type StateManager, type StringifiedState } from "./StateManager";

const StateManagerContext = createContext<StateManager | null>(null);

interface StateManagerProviderProps {
  children: React.ReactNode;
  stringifiedState: StringifiedState | SerializeFrom<StringifiedState>;
}

export function StateManagerProvider({ children, stringifiedState }: StateManagerProviderProps) {
  const isHydrated = useHydrated();
  const state = useMemo(() => {
    const manager = createStateManager({
      searchParams: new URLSearchParams(stringifiedState.toString()),
    });

    if (!isHydrated) return manager;

    return manager.setState({ browser: { timeZone: getLocalTimeZone() } });
  }, [isHydrated, stringifiedState]);

  if (isHydrated) {
    window["rootStateManager"] = state;
  }

  return <StateManagerContext.Provider value={state}>{children}</StateManagerContext.Provider>;
}

type DebugGlobals = {
  rootStateManager: StateManager;
};

declare global {
  interface Window extends DebugGlobals {}
}

export function useStateManager() {
  const state = useContext(StateManagerContext);
  invariant(state, "StateManagerProvider is not set");

  return state;
}
